/**
 * Printable A4 wave pick list. QZ splits on .wave-pick-page.
 */
import { PACKING_SLIP_STORE_NAME } from './packingSlipHtml';

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const formatWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Europe/London',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso || '');
  }
};

export function buildWavePickListHtml(wave, options = {}) {
  const lines = Array.isArray(wave?.lines) ? wave.lines : [];
  const shipping = (wave?.shippingMethods || [])
    .map((entry) => `${entry.method} (${entry.orderCount})`)
    .join(' · ');
  const stack = Array.isArray(options.stack) ? options.stack : [];
  const pages = [];
  const rowsPerPage = 22;
  const pageCount = Math.max(1, Math.ceil(lines.length / rowsPerPage) || 1);

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const slice = lines.slice(pageIndex * rowsPerPage, (pageIndex + 1) * rowsPerPage);
    const body = slice
      .map(
        (line) => `
      <tr>
        <td class="tick"><span></span></td>
        <td class="qty">${escapeHtml(line.quantity)}</td>
        <td>
          <b>${escapeHtml(line.name || line.sku)}</b>
          <small>${escapeHtml(line.sku)}${line.packSize ? ` · ${escapeHtml(line.packSize)}` : ''}</small>
          ${
            line.lots?.length
              ? `<small class="lot">Lot ${escapeHtml(line.lots.join(', '))}</small>`
              : ''
          }
          ${
            line.useBy?.length
              ? `<small class="lot">Use by ${escapeHtml(line.useBy.join(', '))}</small>`
              : ''
          }
        </td>
        <td class="orders">${escapeHtml(line.orderCount)}</td>
        <td class="who">${(line.orders || [])
          .slice(0, 8)
          .map(
            (entry) =>
              `#${escapeHtml(entry.orderKey)}${entry.company ? ` ${escapeHtml(entry.company)}` : ''} ×${escapeHtml(entry.quantity)}`,
          )
          .join('<br/>')}${
          (line.orders || []).length > 8
            ? `<br/><small>+${(line.orders || []).length - 8} more</small>`
            : ''
        }</td>
      </tr>`,
      )
      .join('');

    pages.push(`
<article class="wave-pick-page packing-slip-page" data-print-page="wave-pick" data-page-index="${pageIndex + 1}" data-page-count="${pageCount}">
  <header>
    <p class="brand">${escapeHtml(PACKING_SLIP_STORE_NAME)}</p>
    <h1>Wave pick list</h1>
    <p class="meta">
      ${escapeHtml(wave?.folderLabel || 'Current folder')}
      · ${escapeHtml(wave?.orderCount || 0)} order${wave?.orderCount === 1 ? '' : 's'}
      · ${escapeHtml(wave?.skuCount || 0)} SKU${wave?.skuCount === 1 ? '' : 's'}
      · ${escapeHtml(wave?.unitCount || 0)} units
      · Page ${pageIndex + 1}/${pageCount}
    </p>
    <p class="meta">${escapeHtml(shipping || 'Shipping mixed or unspecified')} · ${escapeHtml(formatWhen(wave?.generatedAt))}</p>
    ${
      pageIndex === 0 && stack.length
        ? `<ol class="stack">${stack
            .map((step) => `<li><b>${escapeHtml(step.label)}</b> ${escapeHtml(step.detail || '')}</li>`)
            .join('')}</ol>`
        : ''
    }
  </header>
  <table>
    <thead>
      <tr>
        <th class="tick">✓</th>
        <th class="qty">Qty</th>
        <th>Product</th>
        <th class="orders">Orders</th>
        <th class="who">Goes to</th>
      </tr>
    </thead>
    <tbody>${body || '<tr><td colspan="5">No product lines on this wave.</td></tr>'}</tbody>
  </table>
  <footer>Walk once by SKU. Pack to the order slips next. London Smoke &amp; Cure.</footer>
</article>`);
  }

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Wave pick list</title>
  <style>
    @page { size: A4 portrait; margin: 0; }
    html, body { margin: 0; padding: 0; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #172220;
      font-size: 10pt;
    }
    .wave-pick-page {
      box-sizing: border-box;
      width: 210mm;
      min-height: 297mm;
      padding: 15mm;
      page-break-after: always;
      break-after: page;
    }
    .wave-pick-page:last-child { page-break-after: auto; break-after: auto; }
    .brand { margin: 0; letter-spacing: 0.14em; text-transform: uppercase; font-size: 9pt; color: #8c6828; font-weight: 800; }
    h1 { margin: 2mm 0 2mm; font-size: 22pt; line-height: 1.05; text-transform: uppercase; }
    .meta { margin: 0 0 2mm; color: #596561; }
    .stack { margin: 0 0 4mm; padding-left: 4.5mm; }
    .stack li { margin: 0 0 1mm; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border-bottom: 0.3mm solid #c7cecb; padding: 2.2mm 1.5mm; vertical-align: top; text-align: left; }
    th { font-size: 8pt; letter-spacing: 0.06em; text-transform: uppercase; color: #596561; }
    td b { display: block; }
    td small { display: block; color: #596561; font-size: 8pt; }
    td small.lot { color: #8c6828; }
    .tick { width: 9mm; }
    .tick span {
      display: inline-block;
      width: 4.5mm;
      height: 4.5mm;
      border: 0.4mm solid #172220;
    }
    .qty { width: 14mm; font-weight: 800; font-size: 13pt; }
    .orders { width: 16mm; }
    .who { width: 58mm; font-size: 8pt; }
    footer { margin-top: 6mm; color: #596561; font-size: 8pt; }
  </style>
</head>
<body>
${pages.join('\n')}
</body>
</html>`;
}
